import React, { useMemo, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EyeOutlined, PlusOutlined, TeamOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { useEmployeeOptions } from './shared/useEmployeeOptions';
import { LEAVE_STATUS_COLOR, hrmsStatusColor } from './shared/hrmsStatus';

const { Text } = Typography;

const LeavesPage: React.FC = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<string>();
  const [employeeId, setEmployeeId] = useState<string>();
  const { options: empOptions, empLabel } = useEmployeeOptions();

  const listParams = useMemo(
    () => ({
      status: statusFilter || undefined,
      employee_id: employeeId || undefined,
    }),
    [statusFilter, employeeId]
  );

  const { data: rows = [], isLoading } = useQuery({
    queryKey: hrmsKeys.leaveApplications(listParams),
    queryFn: () => hrmsService.listLeaveApplications(listParams),
  });

  const stats = useMemo(() => {
    const total = rows.length;
    const pending = rows.filter((r: any) => String(r.status).startsWith('Pending') || r.status === 'Draft').length;
    const approved = rows.filter((r: any) => r.status === 'Approved').length;
    const closed = rows.filter((r: any) => r.status === 'Closed' || r.status === 'DutyResumed').length;
    return { total, pending, approved, closed };
  }, [rows]);

  const columns: ColumnsType<any> = [
    {
      title: 'Application #',
      dataIndex: 'leave_application_number',
      width: 140,
      fixed: 'left',
      render: (v: string, r) => (
        <a onClick={() => navigate(`/hr/leaves/${r.id}`)}>{v || 'Open'}</a>
      ),
    },
    {
      title: 'Employee',
      dataIndex: 'employee_id',
      ellipsis: true,
      render: (id: string) => empLabel(id),
    },
    { title: 'Type', dataIndex: 'leave_type', width: 110 },
    {
      title: 'From',
      dataIndex: 'from_date',
      width: 110,
      render: (d: string) => (d ? dayjs(d).format('DD-MMM-YYYY') : '—'),
    },
    {
      title: 'To',
      dataIndex: 'to_date',
      width: 110,
      render: (d: string) => (d ? dayjs(d).format('DD-MMM-YYYY') : '—'),
    },
    { title: 'Days', dataIndex: 'total_days', width: 70 },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 120,
      render: (s: string) => <Tag color={hrmsStatusColor(s, LEAVE_STATUS_COLOR)}>{s}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 90,
      render: (_, r) => (
        <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => navigate(`/hr/leaves/${r.id}`)}>
          View
        </Button>
      ),
    },
  ];

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Leave applications"
          subtitle="Employee leave requests, approvals, duty resume, and bulk absence"
          primaryActions={
            <Space>
              <Button onClick={() => navigate('/hr/leaves/bulk')}>Bulk absence</Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/hr/leaves/new')}>
                New leave
              </Button>
            </Space>
          }
        />
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Leave workflow"
        description={
          <span>
            Draft → Submit for approval → Approved → Duty resume when the employee returns. Annual entitlement and
            leave types come from <Link to="/hr/setup">HRMS Setup</Link>. Use Bulk absence for site shutdowns or
            common leave days across many employees.
          </span>
        }
      />
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="Total" value={stats.total} prefix={<TeamOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="Draft / Pending" value={stats.pending} valueStyle={{ color: '#1677ff' }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="Approved" value={stats.approved} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="Resumed / Closed" value={stats.closed} />
          </Card>
        </Col>
      </Row>

      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap>
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 160 }}
            value={statusFilter}
            onChange={setStatusFilter}
            options={['Draft', 'Pending', 'Approved', 'Rejected', 'DutyResumed', 'Closed'].map((v) => ({
              value: v,
              label: v,
            }))}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Employee"
            style={{ width: 280 }}
            value={employeeId}
            onChange={setEmployeeId}
            options={empOptions}
          />
          <Text type="secondary">{rows.length} record(s)</Text>
        </Space>
      </Card>

      <Table
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={rows}
        scroll={{ x: 1000 }}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        onRow={(r) => ({
          onClick: () => navigate(`/hr/leaves/${r.id}`),
          style: { cursor: 'pointer' },
        })}
      />
    </WorkspaceLayout>
  );
};

export default LeavesPage;
